import React, {useEffect, useState} from 'react';
import ButtonSolid from 'components/Buttons/ButtonSolid';
import Analytics from 'utils/Analytics';
import {PageModel} from 'survey-core';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {useSurveyError} from 'screens/InsomniaSurvey/contexts/SurveyErrorContext';

const getNextDelayMs = (page: PageModel) => {
  //@ts-expect-error We need to access the custom `riseProperties` property, and this is the only way I know how.
  const {delayOnNext} = page?.jsonObj?.riseProperties ?? {};
  return delayOnNext;
};

const getOnNextCallback = (page: PageModel) => {
  //@ts-expect-error We need to access the custom `riseProperties` property, and this is the only way I know how.
  const {onNext} = page?.jsonObj?.riseProperties ?? {};
  return onNext;
};

const allQuestionsAnswered = (page: PageModel) => {
  return page.questions.every((question: any) => {
    // If the question is not required, skip validation for it
    if (!question.isRequired) {
      return true;
    }
    // For required questions, check if they have a value with length > 0
    return question.value !== undefined && question.value?.length > 0;
  });
};

const trackTextAnswersSubmitted = (page: PageModel) => {
  page.questions
    .filter((question: any) => question.getType?.() === 'text')
    .forEach((question: any) => {
      Analytics.track('Answer selected', {
        survey_name: surveyInstance.title,
        question_title: question.title,
        question_name: question.name,
        question_value: question.value,
        pageNumber: surveyInstance.currentPageNo,
      });
    });
};

export const NextButton: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const {setError} = useSurveyError();
  const page = surveyInstance.currentPage;
  const nextDelayMs = getNextDelayMs(page);

  useEffect(() => {
    // When the screen loads, check if all questions are answered
    // if so, enable the button
    if (allQuestionsAnswered(page)) {
      setDisabled(false);
    }

    const handleValueChanged = (sender: any) => {
      setDisabled(!allQuestionsAnswered(sender.currentPage));
      // Clear error when user changes values
      setError(null);
    };
    // when going back and there is a value select, enable the button
    surveyInstance.onCurrentPageChanged.add(handleValueChanged);
    // when a value is changed, enable the button
    surveyInstance.onValueChanged.add(handleValueChanged);
    return () => {
      surveyInstance.onCurrentPageChanged.remove(handleValueChanged);
      surveyInstance.onValueChanged.remove(handleValueChanged);
    };
  }, [page, setError]);

  return (
    <ButtonSolid
      title={'Next'}
      loading={loading}
      disabled={disabled}
      onClick={async () => {
        const page = surveyInstance.currentPage;
        setDisabled(true);
        setError(null);
        Analytics.track('Next clicked', {
          survey_name: surveyInstance.title,
          question_title: page.questions[0].title,
          question_name: page.questions[0].name,
          question_value: page.questions[0].value,
          pageNumber: surveyInstance.currentPageNo,
        });

        // Call the onNext callback if it exists
        const onNext = getOnNextCallback(page);
        if (onNext) {
          try {
            await onNext(surveyInstance.data);
          } catch (error) {
            console.error('Error in onNext callback:', error);
            const errorMessage = error instanceof Error ? error.message : 'An error occurred. Please try again.';
            setError(errorMessage);
            setDisabled(false);
            return; // Stop execution and don't advance to the next page
          }
        }

        trackTextAnswersSubmitted(page);

        if (nextDelayMs > 0) {
          setLoading(true);
          setTimeout(() => {
            setLoading(false);
            surveyInstance?.nextPage();
          }, nextDelayMs);
        } else {
          surveyInstance?.nextPage();
        }
      }}
    />
  );
};
